import { useEffect, useRef, useState, type RefObject } from "react";
import { QChartBox } from "@coreapp/CoreApp/QChartBox";
import { QBoxLayout } from "@coreapp/CoreApp/QBoxLayout";
import { enumChartBoxInteractionMode } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";

/**
 * Minimal panel stub satisfying QChartBox.InitChartBox requirements.
 * Replicates only what QBoxStylesPanel provides during box style rendering.
 */
class BoxStylePreviewPanel {
  fRenderBoxSpacing = false;
  fNoHighlight = true;
  fAltCanvas = null;
  fElements: any[] = [];
  fZPlanes: any[] | null = null;

  PushElement() {}
  RemoveElement() {}
  GetPreviewMode() {
    return false;
  }
  BoxSelectEnabled() {
    return false;
  }
  BoxDragEnabled() {
    return false;
  }
  BoxIsControl() {
    return false;
  }
  DoSelectElement() {}
  SetMarqueeEnabled() {}
  IsElementInLayer() {
    return true;
  }
}

/**
 * Render a QChartBox element and all its children to a canvas,
 * replicating QPanel.RenderElementTree without the full panel pipeline.
 */
function renderElementTree(element: any, canvas: HTMLCanvasElement) {
  if (!element.IsShown()) return;
  element.RenderElement(canvas);
  if (element.fElements) {
    for (const child of element.fElements) {
      if (child.IsShown()) {
        renderElementTree(child, canvas);
      }
    }
  }
  element.DoPostRender(canvas);
}

interface PreviewDimensions {
  width: number;
  height: number;
}

interface UseBoxStylePreviewOptions {
  /** Maximum display height in CSS pixels. Tall layouts scale down; short layouts keep native size. Width is always derived from aspect ratio. */
  maxPreviewHeight?: number;
}

function computeDisplaySize(
  nativeW: number,
  nativeH: number,
  maxH: number | undefined
): PreviewDimensions {
  // Height-only scale; width is clamped in CSS (max-width: 100%). Measuring the parent's
  // clientWidth here was unreliable mid RSB collapse/expand and produced tiny previews.
  const scale = maxH && nativeH > maxH ? maxH / nativeH : 1;
  if (scale >= 1) return { width: nativeW, height: nativeH };
  return { width: Math.round(nativeW * scale), height: Math.round(nativeH * scale) };
}

/**
 * Renders a box style layout preview to a canvas ref.
 * Follows the same pattern as QBoxStylesPanel.AddContent:
 * uses the ORIGINAL layout (preserving fIsBoxStyle=true),
 * creates QChartBox with null dataObj in boxstyle mode.
 *
 * Uses IntersectionObserver for lazy rendering — canvas is only
 * drawn when it scrolls into view.
 */
export function useBoxStylePreview(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  layout: QBoxLayout | null,
  options?: UseBoxStylePreviewOptions,
  version?: number
): PreviewDimensions {
  const panelRef = useRef(new BoxStylePreviewPanel());
  const renderedRef = useRef(false);
  const [dimensions, setDimensions] = useState<PreviewDimensions>({ width: 0, height: 0 });

  // Compute display dimensions once layout is available
  useEffect(() => {
    if (!layout) return;

    const outset = layout.GetBorderOutset();
    const nativeW = layout.GetBoxWidth() + outset * 2;
    const nativeH = layout.GetBoxHeight(false) + outset * 2;

    const canvas = canvasRef.current;
    const { width: displayW, height: displayH } = computeDisplaySize(
      nativeW,
      nativeH,
      options?.maxPreviewHeight
    );

    setDimensions({ width: displayW, height: displayH });

    // Size the canvas element immediately so layout doesn't shift when rendering fires
    if (canvas) {
      canvas.style.width = displayW + "px";
      canvas.style.height = displayH + "px";
    }
  }, [layout, options?.maxPreviewHeight, version]);

  // Lazy render via IntersectionObserver
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layout) return;

    renderedRef.current = false;

    const render = () => {
      if (renderedRef.current) return;
      renderedRef.current = true;

      const outset = layout.GetBorderOutset();
      const nativeW = layout.GetBoxWidth() + outset * 2;
      const nativeH = layout.GetBoxHeight(false) + outset * 2;

      const dpr = Globals._dpr || window.devicePixelRatio || 1;
      canvas.width = Math.floor(nativeW * dpr);
      canvas.height = Math.floor(nativeH * dpr);

      // Display size: scale down if taller than max, otherwise use native. Width over-runs are
      // clamped responsively in CSS, not from a measured parent width (see computeDisplaySize).
      const { width: displayW, height: displayH } = computeDisplaySize(
        nativeW,
        nativeH,
        options?.maxPreviewHeight
      );
      canvas.style.width = displayW + "px";
      canvas.style.height = displayH + "px";

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(dpr, dpr);

      const panel = panelRef.current;
      const box = new QChartBox();
      const originalX = layout.GetPositionX();
      const originalY = layout.GetPositionY();
      // The edited box shares this layout instance with the live BLE panel, and InitChartBox
      // overwrites its DBObj — snapshot/restore so the thumbnail can't wipe the preview person.
      const originalDBObj = layout.GetDBObj();
      const originalDBObjID = layout.GetDBObjID();
      try {
        box.InitChartBox(
          layout,
          panel,
          outset, // xPos — offset by border outset
          outset, // yPos — offset by border outset
          enumChartBoxInteractionMode.boxstyle,
          null, // dataObj
          null, // dataObjID
          false, // restoreCellSelection
          null, // wsMgr
          false // lightInit — must be false to trigger CalculateLayout + InitLayoutCells
        );
        box.SetEnableResizer(false);
        box.SetEnableExpander(false);
        box.SetDraggable(false);

        renderElementTree(box, canvas);
      } finally {
        // Restore layout position so temporary preview rendering does not affect other consumers.
        layout.SetPosition(originalX, originalY);
        layout.SetDBObj(originalDBObj);
        layout.SetDBObjID(originalDBObjID);
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      render();
      return () => {};
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            render();
            observer.disconnect();
            return;
          }
        }
      },
      { rootMargin: "200px" } // render slightly before entering viewport
    );

    observer.observe(canvas);
    return () => observer.disconnect();
  }, [canvasRef, layout, options?.maxPreviewHeight, version]);

  return dimensions;
}
